import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Heart button to add/remove a shop from the user's favorites.
 * - Guests see the button; clicking prompts login.
 * - Logged-in users see the current state and can toggle.
 *
 * Emits a global window event "kh-favorites-changed" after each successful toggle,
 * so any favorites list on screen can refresh itself.
 */
export default function FavoriteButton({ shopId, size = "md", className = "", stopPropagation = true }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [fav, setFav] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load initial state from cached ids list (fetched by parent) — fall back to network
  useEffect(() => {
    if (!user?.id || !shopId) return;
    let cancelled = false;
    api.get("/favorites/ids")
      .then(r => { if (!cancelled) setFav((r.data?.ids || []).includes(shopId)); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [shopId, user?.id]);

  const toggle = async (e) => {
    if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
    if (!user?.id) {
      toast.info("سجّل الدخول لإضافة المحل للمفضلة");
      nav("/login");
      return;
    }
    setBusy(true);
    const next = !fav;
    setFav(next);
    try {
      if (next) {
        await api.post(`/favorites/${shopId}`);
        toast.success("تمت الإضافة للمفضلة");
      } else {
        await api.delete(`/favorites/${shopId}`);
      }
      window.dispatchEvent(new CustomEvent("kh-favorites-changed"));
    } catch {
      setFav(!next); // revert
      toast.error("تعذّر تحديث المفضلة");
    } finally {
      setBusy(false);
    }
  };

  const dims = size === "sm" ? "size-8" : size === "lg" ? "size-11" : "size-9";
  const iconDims = size === "sm" ? "size-4" : size === "lg" ? "size-5" : "size-4";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`fav-btn-${shopId}`}
      aria-label={fav ? "إزالة من المفضلة" : "إضافة للمفضلة"}
      className={`${dims} rounded-full flex items-center justify-center transition-all shadow-lg backdrop-blur-md border ${
        fav
          ? "bg-rose-500/95 text-white border-rose-400 hover:bg-rose-500 scale-105"
          : "bg-slate-900/80 text-white/80 border-white/15 hover:bg-slate-900 hover:text-rose-400"
      } ${className}`}
    >
      <Heart className={`${iconDims} transition-transform ${fav ? "fill-white scale-110" : ""}`} />
    </button>
  );
}
